import React, { useEffect, useRef, useState } from "react";
import { Box, Divider, Grid, List, ListItem, ListItemText, Typography, styled, useMediaQuery, useTheme } from "@mui/material";
import image1 from "../../../../assets/editorial/editorial1.jpeg";
import Footer from "../../../../common/Footer";
import Header from "../../../../common/Header";
import { useNavigate } from "react-router-dom";
import LeftSideNavigation from "../../../../common/LeftSideNavigation";

const ScrollContainer = styled("div")(({ theme }) => ({
  display: "flex",
  overflowX: "auto",
  overflowY: "hidden",
  scrollSnapType: "x mandatory",
  scrollBehavior: "smooth",
  height: "100%",
  width: "100%",
  position: "relative",
  margin: 0,
  padding: 0,
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": {
    display: "none",
  },
}));

const ScrollItem = styled("div")({
  flex: "0 0 100%",
  scrollSnapAlign: "start",
  height: "100%",
  position: "relative",
  margin: 0,
  padding: 0,
});

const ScrollImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
  margin: 0,
  padding: 0,
});

const DotsContainer = styled("div")(({ theme }) => ({
  position: "absolute",
  bottom: "20px",
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  gap: "10px",
  zIndex: 10,
  pointerEvents: "auto",
}));

const Dot = styled("div")(({ active }) => ({
  width: "12px",
  height: "12px",
  borderRadius: "50%",
  cursor: "pointer",
  backgroundColor: active ? "#fff" : "rgba(255,255,255,0.5)",
  transition: "background-color 0.3s ease",
  "&:hover": {
    backgroundColor: "white",
    transform: "scale(1.2)",
  },
}));

const Editorial = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const scrollRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();
  const images = [image1];
  const [scrolled, setScrolled] = useState(false);
  const contentRef = useRef(null);

  const handleDotClick = (index) => {
    const container = scrollRef.current;
    if (!container) return;
    const scrollWidth = container.scrollWidth;
    const itemWidth = scrollWidth / images.length;
    container.scrollTo({ left: itemWidth * index, behavior: "smooth" });
    setCurrentIndex(index);
  };

  const handleScroll = () => {
    const container = scrollRef.current;
    if (!container) return;
    const scrollLeft = container.scrollLeft;
    const itemWidth = container.offsetWidth;
    const index = Math.round(scrollLeft / itemWidth);
    setCurrentIndex(index);
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setScrolled(scrollTop > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    window.scroll(0, 0);
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const interval = setInterval(() => {
      const nextIndex = (currentIndex + 1) % images.length;
      setCurrentIndex(nextIndex);
      container.scrollTo({
        left: container.clientWidth * nextIndex,
        behavior: "smooth",
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [currentIndex, images.length]);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const handleScroll = () => {
      const scrollWidth = container.scrollWidth;
      const itemWidth = scrollWidth / images.length;
      const newIndex = Math.round(container.scrollLeft / itemWidth);
      setCurrentIndex(newIndex);
    };

    container.addEventListener("scroll", handleScroll);
    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <Header />

      {/* Hero Section - scrolls away normally */}
      <Box
        sx={{
          width: "100%",
          height: isMobile ? "50vh" : "65vh",
          position: "relative",
        }}
      >
        <ScrollContainer ref={scrollRef}>
          {images.map((img, index) => (
            <ScrollItem key={index}>
              <ScrollImage src={img} alt={`Banner ${index + 1}`} />
            </ScrollItem>
          ))}
        </ScrollContainer>

        <DotsContainer>
          {images.map((_, idx) => (
            <Dot
              key={idx}
              active={currentIndex === idx}
              onClick={() => handleDotClick(idx)}
            />
          ))}
        </DotsContainer>
      </Box>

      {/* Main Content Section */}
      <Grid container sx={{
        px: isMobile ? 2 : 4,
        py: isMobile ? 2 : 4,
        position: 'relative',
        maxWidth: '100%',
        margin: '0 auto',
      }}>
        {/* Left Side Navigation - Sticky */}
        {!isMobile && (
          <Grid item md={2} sx={{
            position: 'sticky',
            top: 80,
            height: 'calc(100vh - 64px)',
            alignSelf: 'flex-start',
            overflowY: 'auto',
            zIndex: 1,
            pr: 2,
          }}>
            <LeftSideNavigation />
          </Grid>
        )}

        {/* Right Side Content - Scrollable */}
        <Grid item xs={12} md={10} sx={{
          border: '1px solid #7f6b56',
          overflowY: 'auto',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': {
            display: 'none'
          }
        }}>
          <Box
            sx={{
              color: "#7f6b56",
              fontFamily: "Inter, sans-serif",
              px: isMobile ? 2 : 3,
              py: isMobile ? 3 : 6,
              position: "relative",
            }}
          >
            <Typography
              variant={isMobile ? "h5" : "h4"}
              fontWeight="bold"
              gutterBottom
              sx={{ fontFamily: "Montserrat, sans-serif" }}
            >
              World Reef Awareness Day 2025: Bringing Corals Back to Life in the Lakshadweep Archipelago
            </Typography>

            <Typography
              variant="body1"
              gutterBottom
              sx={{
                mt: 2,
                lineHeight: 1.6,
                fontFamily: "Montserrat, sans-serif",
                fontSize: isMobile ? "16px" : "20px",
                fontWeight: "500",
                fontStyle: "italic"
              }}
            >
              By Mohammed Nowshad, Chairman, REEF
            </Typography>

            <Typography
              variant="body1"
              gutterBottom
              sx={{
                mt: 2,
                lineHeight: 1.6,
                fontFamily: "Montserrat, sans-serif",
                fontSize: isMobile ? "16px" : "20px",
                fontWeight: "500",
              }}
            >
              Every year on June 1st, the global community observes World Reef Awareness Day, a moment to reflect on the fragile beauty of coral reefs and the urgent need to protect them. In the Lakshadweep Archipelago, India's sole chain of coral atolls, the message is particularly poignant. This year's theme – <strong>"Bringing Corals Back to Life"</strong> – resonates deeply with our island communities, whose lives, livelihoods, and cultural identity are inextricably linked to the reef.
            </Typography>

            {/* A Living Barrier, a Way of Life */}
            <Typography 
              variant={isMobile ? "h6" : "h5"} 
              fontWeight={600} 
              gutterBottom 
              sx={{ 
                mt: 3, 
                fontFamily: "Montserrat, sans-serif" 
              }}
            >
              A Living Barrier, a Way of Life
            </Typography>
            <Typography 
              variant="body1" 
              gutterBottom 
              sx={{ 
                mt: 1, 
                lineHeight: 1.6, 
                fontFamily: "Montserrat, sans-serif", 
                fontSize: isMobile ? "16px" : "20px", 
                fontWeight: "500" 
              }}
            >
              The reefs of Lakshadweep are not just ecological treasures—they are our natural fortresses. They shield our shores from erosion, serve as custodians of marine biodiversity, support fisheries, and sustain marine life in breathtaking abundance. However, in recent decades, these reefs have come under unprecedented stress due to climate change, coral bleaching, overfishing, and marine pollution.
            </Typography>
            <Typography 
              variant="body1" 
              gutterBottom 
              sx={{ 
                mt: 2, 
                lineHeight: 1.6, 
                fontFamily: "Montserrat, sans-serif", 
                fontSize: isMobile ? "16px" : "20px", 
                fontWeight: "500" 
              }}
            >
              Mass bleaching events in 1998, 2010, and 2016 served as painful reminders of the vulnerability of our coral ecosystems to rising sea surface temperatures. For islanders who have grown up swimming among vibrant coral gardens, the fading colours of the reef represent more than an environmental concern—they signify a loss of identity and heritage.
            </Typography>

            {/* Hope from the Atolls: Local Actions, Lasting Impact */}
            <Typography 
              variant={isMobile ? "h6" : "h5"} 
              fontWeight={600} 
              gutterBottom 
              sx={{ 
                mt: 3, 
                fontFamily: "Montserrat, sans-serif" 
              }}
            >
              Hope from the Atolls: Local Actions, Lasting Impact
            </Typography>
            <Typography 
              variant="body1" 
              gutterBottom 
              sx={{ 
                mt: 1, 
                lineHeight: 1.6, 
                fontFamily: "Montserrat, sans-serif", 
                fontSize: isMobile ? "16px" : "20px", 
                fontWeight: "500" 
              }}
            >
              Despite the challenges, there is hope—growing stronger with every act of stewardship. Across the Lakshadweep islands, youth groups, fishers, scientists, and community leaders are uniting to protect and restore coral reefs.
            </Typography>
            <Typography 
              variant="body1" 
              gutterBottom 
              sx={{ 
                mt: 2, 
                lineHeight: 1.6, 
                fontFamily: "Montserrat, sans-serif", 
                fontSize: isMobile ? "16px" : "20px", 
                fontWeight: "500" 
              }}
            >
              Through reef restoration projects, broken coral fragments are stabilized and nurtured back to health using methods such as coral gardening and rope nurseries. REEF, in collaboration with The Habitat Trust, research institutions, and local panchayats, has been actively engaging island youth in reef monitoring, snorkel-based surveys, and citizen science programs to track reef health.
            </Typography>
            <Typography 
              variant="body1" 
              gutterBottom 
              sx={{ 
                mt: 2, 
                lineHeight: 1.6, 
                fontFamily: "Montserrat, sans-serif", 
                fontSize: isMobile ? "16px" : "20px", 
                fontWeight: "500" 
              }}
            >
              Traditional ecological knowledge is being revived and integrated with scientific conservation efforts. Community elders share stories of past abundance, helping younger generations understand long-term ecological changes and the value of sustainable practices.
            </Typography>

            {/* From Awareness to Action */}
            <Typography 
              variant={isMobile ? "h6" : "h5"} 
              fontWeight={600} 
              gutterBottom 
              sx={{ 
                mt: 3, 
                fontFamily: "Montserrat, sans-serif" 
              }}
            >
              From Awareness to Action
            </Typography>
            <Typography 
              variant="body1" 
              gutterBottom 
              sx={{ 
                mt: 1, 
                lineHeight: 1.6, 
                fontFamily: "Montserrat, sans-serif", 
                fontSize: isMobile ? "16px" : "20px", 
                fontWeight: "500" 
              }}
            >
              World Reef Awareness Day 2025 is more than just a symbolic date—it is a call to action. To truly bring our corals back to life, we must:
            </Typography>
            <Box sx={{ fontFamily: "Montserrat, sans-serif", mb: 2 }}>
              <List sx={{ listStyleType: 'disc', paddingLeft: '1.2em' }}>
                {[
                  { desc: "Reduce global carbon emissions to mitigate ocean warming and acidification." },
                  { desc: "Strengthen Marine Protected Areas (MPAs) in Lakshadweep to support the natural recovery of coral reefs." },
                  { desc: "Promote eco-friendly tourism practices that allow visitors to experience the reef without causing harm." },
                  { desc: "Invest in reef research and education to cultivate the next generation of marine stewards." }
                ].map(({ desc }, index) => (
                  <ListItem sx={{ display: 'list-item' }} disableGutters key={index}>
                    <ListItemText
                      primary={
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontSize: isMobile ? "16px" : "20px", 
                            fontWeight: 500, 
                            fontFamily: "Montserrat, sans-serif", 
                            lineHeight: 1.6 
                          }}
                        >
                          {desc}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
            <Typography 
              variant="body1" 
              gutterBottom 
              sx={{ 
                mt: 2, 
                lineHeight: 1.6, 
                fontFamily: "Montserrat, sans-serif", 
                fontSize: isMobile ? "16px" : "20px", 
                fontWeight: "500" 
              }}
            >
              On June 5th, coinciding with World Environment Day, REEF will host a series of island-wide events, including underwater clean-ups, student awareness rallies, and storytelling circles that celebrate our rich reef heritage. These activities are not just for visibility—they represent a continued commitment to living in harmony with the ocean.
            </Typography>

            {/* A Shared Future with the Reef */}
            <Typography 
              variant={isMobile ? "h6" : "h5"} 
              fontWeight={600} 
              gutterBottom 
              sx={{ 
                mt: 3, 
                fontFamily: "Montserrat, sans-serif" 
              }}
            >
              A Shared Future with the Reef
            </Typography>
            <Typography 
              variant="body1" 
              gutterBottom 
              sx={{ 
                mt: 1, 
                lineHeight: 1.6, 
                fontFamily: "Montserrat, sans-serif", 
                fontSize: isMobile ? "16px" : "20px", 
                fontWeight: "500" 
              }}
            >
              Coral reefs are resilient, but they need our help. In the Lakshadweep Archipelago, we are at the forefront of both the challenge and the solution. As we celebrate World Reef Awareness Day 2025, let's reaffirm our commitment to protecting these living structures of hope. When we bring corals back to life, we also revive our connection to the sea and secure a future for the islands and generations to come.
            </Typography>
          </Box>
        </Grid>

        {/* Mobile Navigation - appears after content */}
        {isMobile && (
          <Grid item xs={12} sx={{ mt: 2 }}>
            <LeftSideNavigation />
          </Grid>
        )}
      </Grid>

      <Footer variant="light" />
    </>
  );
};

export default Editorial;